/**
 * 伝票番号の採番 (v4.0.0)
 *
 * 形式: {営業日}-{連番}  例: 20260902-0043
 *
 * 【なぜトランザクションで採番するか】
 * 番号は「連続していること」に意味がある。抜けや重複があると、
 * 取消が「日常の操作」なのか「記録を消した跡」なのか区別できなくなる。
 * レジが複数ある店では、番号を数えて振ると必ず衝突するので、
 * 営業日ごとのカウンタ文書を tx.get / tx.set で進める。
 *
 * 【取消しても番号は詰めない】
 * 取り消した番号は欠番にせず、その番号のまま「取消済み」として残す。
 * 詰め直すと、取消が存在した事実そのものが消える。
 */

import { doc, runTransaction, type Firestore, Timestamp } from 'firebase/firestore';

export interface SlipNumberConfig {
  /** 番号の桁数（既定4桁 → 0001〜9999） */
  digits?: number;
  /** 接頭辞。レジごとに分けたい場合に使う（例: 'A'） */
  prefix?: string;
}

export function formatSlipNumber(
  businessDate: string,
  seq: number,
  config: SlipNumberConfig = {}
): string {
  const digits = config.digits ?? 4;
  const compact = businessDate.replace(/-/g, '');
  const body = String(seq).padStart(digits, '0');
  return config.prefix ? `${config.prefix}${compact}-${body}` : `${compact}-${body}`;
}

/**
 * 次の伝票番号を採番する。
 * 同じ営業日のカウンタを1つ進めて返す。レジが同時に叩いても重複しない。
 */
export async function issueSlipNumber(
  db: Firestore,
  shopId: string,
  businessDate: string,
  config: SlipNumberConfig = {}
): Promise<{ slipNumber: string; seq: number }> {
  const ref = doc(db, 'slipCounters', `${shopId}__${businessDate}`);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? ((snap.data() as any).lastSeq ?? 0) : 0;
    const seq = current + 1;
    tx.set(ref, {
      shopId,
      businessDate,
      lastSeq: seq,
      updatedAt: Timestamp.now(),
    }, { merge: true });
    return { slipNumber: formatSlipNumber(businessDate, seq, config), seq };
  });
}

export interface SlipSummary {
  slipNumber: string;
  seq: number;
  voided: boolean;
}

export interface ContinuityReport {
  businessDate: string;
  issued: number;
  voided: number;
  /** 採番されたのに伝票が存在しない番号。運用ミスか障害の跡 */
  missing: number[];
  /** 同じ番号が2枚以上ある。採番の競合 */
  duplicated: number[];
  ok: boolean;
}

/**
 * 伝票番号の連続性を検査する。
 *
 * 「取消が何件あるか」ではなく「番号に穴が空いていないか」を見る。
 * 取消済みは番号として存在するので穴にはならない。
 * 穴が空くのは、採番だけして伝票を書かずに落ちた場合か、記録が失われた場合。
 * 日次締めのときにこれを出す。
 */
export function checkContinuity(
  businessDate: string,
  slips: SlipSummary[]
): ContinuityReport {
  const seqs = slips.map((s) => s.seq).filter((n) => Number.isFinite(n));
  const counts = new Map<number, number>();
  seqs.forEach((n) => counts.set(n, (counts.get(n) ?? 0) + 1));

  const max = seqs.length > 0 ? Math.max.apply(null, seqs) : 0;
  const missing: number[] = [];
  for (let i = 1; i <= max; i++) {
    if (!counts.has(i)) missing.push(i);
  }
  const duplicated = Array.from(counts.entries())
    .filter(([, c]) => c > 1)
    .map(([n]) => n)
    .sort((a, b) => a - b);

  return {
    businessDate,
    issued: max,
    voided: slips.filter((s) => s.voided).length,
    missing,
    duplicated,
    ok: missing.length === 0 && duplicated.length === 0,
  };
}

/** 連続性レポートを、締め画面に出す日本語にする */
export function describeContinuity(r: ContinuityReport): string {
  if (r.ok) {
    return r.voided > 0
      ? `伝票番号 1〜${r.issued} が揃っています（うち取消 ${r.voided} 件）。抜けはありません。`
      : `伝票番号 1〜${r.issued} が揃っています。抜けも取消もありません。`;
  }
  const parts: string[] = [];
  if (r.missing.length > 0) {
    parts.push(
      `番号が抜けています: ${r.missing.slice(0, 20).join(', ')}` +
      (r.missing.length > 20 ? ` ほか${r.missing.length - 20}件` : '') +
      '。採番だけして伝票が保存されなかったか、記録が失われた可能性があります。'
    );
  }
  if (r.duplicated.length > 0) {
    parts.push(`番号が重複しています: ${r.duplicated.join(', ')}。採番が競合した可能性があります。`);
  }
  return parts.join('\n');
}
